import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Calendar, ChevronDown, Info } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { StepProps } from "@/lib/builder/models/builder.model";
import { AccommodationCard } from "./activities-card/AccommodationCard";
import { ActivityCard } from "./activities-card/ActivityCard";
import { FlightCard } from "./activities-card/FlightCard";
import { TransportationCard } from "./activities-card/TransportationCard";
import { FoodDrinksCard } from "./activities-card/FoodDrinksCard";
import { EventsCard } from "./activities-card/EventsCard";
import { NotesCard } from "./activities-card/NotesCard";
import { useModalCallbacks } from "./hooks/useModalCallbacks";
import { ItineraryBuilder, ItineraryDayBuilder } from "./models/itinerary-builder.model";
import { ItineraryInsights } from "./components/ItineraryInsights";
import { DayInsights } from "./components/DayInsights";
import {
  ActivityType,
  AccommodationActivity,
  ActivityActivity,
  FlightActivity,
  TransportationActivity,
  FoodDrinksActivity,
  EventsActivity,
  NotesActivity,
  ActivityFactory,
  ItineraryActivity,
  AccommodationActivityClass
} from "./models/activity.model";
import { ACTIVITIES } from "./models/activity-configs.model";
import { createCheckInOutActivities } from "@/components/utils/createCheckOutFromCheckIn";
import React from 'react';

const DayBuilderStep = ({
  data,
  onDataChange,
  canProceed,
  setCanProceed,
  selectedDate,
  isReadOnly = false,
  onBuilderChange
}: StepProps) => {
  // Inizializza il builder dall'itinerario esistente
  const { userRole } = useAuth();

  const builder = useMemo(() => {
    console.log('🚀 DayBuilderStep: Initializing builder with data:', {
      hasData: !!data,
      startDate: data?.start_date,
      endDate: data?.end_date,
      itineraryDaysCount: data?.itinerary_days?.length || 0
    });
    
    const itineraryBuilder = new ItineraryBuilder();
    
    // Controlla che abbiamo le date valide prima di inizializzare
    if (data?.start_date && data?.end_date) {
      if (data.itinerary_days && data.itinerary_days.length > 0) {
        // Passa startDate e endDate per generare tutti i giorni dell'intervallo
        console.log('📋 Loading from array with dates');
        // Convert raw activity objects into typed activity instances via ActivityFactory
        const normalizedDays = data.itinerary_days.map((d: any) => ({
          date: d.date,
          activities: (d.activities || []).map((a: any) => ActivityFactory.create(a as ItineraryActivity))
        }));

        itineraryBuilder.loadFromArray(
          normalizedDays,
          data.start_date,
          data.end_date
        );
      } else {
        // Nessuna attività ancora, ma genera comunque i giorni vuoti
        console.log('📅 Initializing empty days');
        itineraryBuilder.initializeDays(data.start_date, data.end_date);
      }
      console.log('✅ Builder initialized with', itineraryBuilder.getAllDays().length, 'days');
    } else {
      console.warn('⚠️ No dates available in data');
    }
    
    return itineraryBuilder;
  }, [data]);

  // Notify parent about builder changes
  useEffect(() => {
    if (onBuilderChange) {
      onBuilderChange(builder);
    }
  }, [builder, onBuilderChange]);

  const [currentDay, setCurrentDay] = useState<ItineraryDayBuilder | null>(null);
  const [, forceUpdate] = useState({});

  // Aggiorna il giorno corrente quando cambia selectedDate o builder
  useEffect(() => {
    console.log('🔄 Updating currentDay for date:', selectedDate, 'Builder has', builder.getAllDays().length, 'days');
    if (selectedDate) {
      const day = builder.getDay(selectedDate);
      console.log('📍 Day found:', !!day, 'Activities:', day?.activities.length || 0);
      setCurrentDay(day || null);
    }
  }, [selectedDate, builder]);

  // Helper function per aggiornare il data model e forzare re-render
  const updateBuilder = () => {
    // Forza re-render
    forceUpdate({});
    
    // Aggiorna currentDay
    if (selectedDate) {
      setCurrentDay(builder.getDay(selectedDate) || null);
    }
    
    // Notifica il cambio al parent
    onDataChange({ itinerary_days: builder.toArray() });
  };

  // Function to add or update activity
  const addActivities = (activityData: ItineraryActivity, isEditing?: boolean, editingActivityId?: string) => {
    if (!selectedDate) return;

    // Se è editing, rimuovi l'attività esistente
    if (isEditing && editingActivityId) {
      builder.removeActivityById(editingActivityId);
    }

    // Determina la data effettiva dell'attività
    const activityDate = (activityData.start_date as string) || (activityData.date as string) || selectedDate;
    const dateStr = activityDate.split('T')[0];

    // IMPORTANTE: Imposta il campo date sull'attività prima di crearla
    // Questo assicura che ogni attività abbia sempre il campo date valorizzato correttamente
    const activityDataWithDate = {
      ...activityData,
      date: dateStr
    };

    // Crea l'attività tipizzata usando ActivityFactory
    const activity = ActivityFactory.create(activityDataWithDate as ItineraryActivity);

    // GESTIONE ACCOMMODATION
    if (activity instanceof AccommodationActivityClass) {
      const checkInOut = createCheckInOutActivities(activity as AccommodationActivityClass);
      const checkInActivity = ActivityFactory.create(checkInOut[0] as ItineraryActivity);
      const checkOutActivity = ActivityFactory.create(checkInOut[1] as ItineraryActivity);
      
      // Aggiungi check-in e check-out ai rispettivi giorni
      builder.addActivity(checkInOut[0].start_date, checkInActivity as ItineraryActivity);
      builder.addActivity(checkInOut[1].end_date, checkOutActivity as ItineraryActivity);
    } else {
      // Aggiungi attività normale
      builder.addActivity(dateStr, activity as ItineraryActivity);
    }

    updateBuilder();
  };

  // derive itinerary bounds from builder
  const itineraryStartDate = builder.startDate;
  const itineraryEndDate = builder.endDate;

  const modalCallbacks = useModalCallbacks({
    onSave: addActivities,
    itineraryStartDate,
    itineraryEndDate,
    itineraryId: data?.id || ''
  });

  const { language } = useI18n();
  const [isDragging, setIsDragging] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const dragStartTimer = useRef<number | null>(null);

  // Listen to global drag events to detect when the user drags out of the window
  // (failed drop) so we can hide external drop zones and clear timers/placeholders.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onWindowDragLeave = (e: DragEvent) => {
      // When the drag leaves the window most browsers emit a dragleave with pageX/Y === 0
      // Use that as a signal that the user dragged outside the app (likely a failed drop).
      // Clear any pending timers and reset drag UI state.
      try {
        // pageX/pageY may be present on DragEvent
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (e.pageX === 0 && e.pageY === 0) {
          if (dragStartTimer.current) {
            window.clearTimeout(dragStartTimer.current);
            dragStartTimer.current = null;
          }
          setIsDragging(false);
          setDraggingId(null);
        }
      } catch (err) {
        // swallow any unexpected errors - recovery is simple (resetting state)
        if (dragStartTimer.current) {
          window.clearTimeout(dragStartTimer.current);
          dragStartTimer.current = null;
        }
        setIsDragging(false);
        setDraggingId(null);
      }
    };

    const onWindowDropOrEnd = () => {
      if (dragStartTimer.current) {
        window.clearTimeout(dragStartTimer.current);
        dragStartTimer.current = null;
      }
      setIsDragging(false);
      setDraggingId(null);
    };

    window.addEventListener('dragleave', onWindowDragLeave);
    // Listen to 'drop' and 'dragend' globally as a fallback when dropping outside
    window.addEventListener('drop', onWindowDropOrEnd);
    window.addEventListener('dragend', onWindowDropOrEnd);

    return () => {
      window.removeEventListener('dragleave', onWindowDragLeave);
      window.removeEventListener('drop', onWindowDropOrEnd);
      window.removeEventListener('dragend', onWindowDropOrEnd);
    };
  }, []);

  // Update canProceed based on whether we have at least one activity
  useEffect(() => {
    const hasActivities = builder.isValid();
    setCanProceed(hasActivities);
  }, [builder, setCanProceed]);

  const handleAddActivity = (activityType: ActivityType) => {
    modalCallbacks.openModal(activityType);
  };

  const deleteActivity = (date: string, activity: ItineraryActivity) => {
    builder.removeActivityById(activity.id);
    updateBuilder();
  };

  const duplicateActivity = (date: string, activity: ItineraryActivity) => {
    const day = builder.getDay(date);
    if (!day) return;

    const activityToDuplicate = day.findActivity(activity.id);
    if (!activityToDuplicate) return;

    const duplicatedActivity = ActivityFactory.create({
      ...activityToDuplicate,
      id: crypto.randomUUID(),
      title: `${activityToDuplicate.title} (Copy)`
    } as ItineraryActivity);

    builder.addActivity(date, duplicatedActivity as ItineraryActivity);
    updateBuilder();
  };

  const formatTitle = (day: string) => {
    const date = new Date(day);
    const dayNumber = date.getDate();
    const locale = language || undefined;
    const monthName = date.toLocaleDateString(locale, { month: 'long' });
    return `${dayNumber} ${monthName}`;
  };

  const formatDescription = (day: string) => {
    const date = new Date(day);
    const locale = language || undefined;
    const dayName = date.toLocaleDateString(locale, { weekday: 'long' });
    return `${dayName}`;
  };

  // External drop zone rendered between cards and at the start/end of the list
  const DropZone = ({ onDrop, visible }: { onDrop: (sourceId: string) => void; visible: boolean }) => {
    const [hover, setHover] = React.useState(false);
    // Render the drop zone but hide it when not dragging
    return (
      <div
        className={`w-full h-6 my-0.5 rounded-md border-2 transition-colors flex items-center justify-center ${visible ? (hover ? 'border-sky-400 bg-sky-50' : 'border-dashed border-gray-200 bg-transparent') : 'hidden'}`}
        onDragOver={(e) => { e.preventDefault(); setHover(true); }}
        onDragEnter={(e) => { e.preventDefault(); setHover(true); }}
        onDragLeave={() => setHover(false)}
        onDrop={(e) => {
          e.preventDefault();
          setHover(false);
          const sourceId = e.dataTransfer.getData('text/plain');
          if (sourceId) onDrop(sourceId);
        }}
      >
        {/* visual hint when hovering */}
        {hover ? <span className="text-xs text-sky-600">Drop here</span> : null}
      </div>
    );
  };

  // Function to render the correct card based on activity type
  const renderActivityCard = (activity: ItineraryActivity) => {
    const commonProps = {
      onEdit: (activity: ItineraryActivity) => {
        modalCallbacks.openModal(activity.type, undefined, activity);
      },
      onDelete: (activity: ItineraryActivity) => deleteActivity(currentDay!.date, activity),
      onDuplicate: (activity: ItineraryActivity) => duplicateActivity(currentDay!.date, activity),
      onExpand: () => { },
      isReadOnly,
      isTraveler: userRole === 'traveler',
      isAdmin: userRole === 'admin',
      // drag handlers
        onDragStart: (act: ItineraryActivity) => {
          // Delay showing drop zones and setting the placeholder id slightly to avoid
          // removing the dragged element from the DOM before the native drag image
          // is captured (which can cancel the drag). We set both inside the same
          // short timer so the dragstart can complete first.
          if (dragStartTimer.current) window.clearTimeout(dragStartTimer.current);
          dragStartTimer.current = window.setTimeout(() => {
            setIsDragging(true);
            setDraggingId(act.id);
            dragStartTimer.current = null;
          }, 0);
        },
        onDragEnd: () => {
          // Cancel pending timer if any, and hide drop zones
          if (dragStartTimer.current) {
            window.clearTimeout(dragStartTimer.current);
            dragStartTimer.current = null;
          }
          setIsDragging(false);
          setDraggingId(null);
        },
        onDropBefore: (sourceId: string, targetId: string | undefined) => {
          console.log('drop', sourceId, 'before', targetId, 'on day', currentDay?.date);
          if (!currentDay) return;
          const moved = builder.moveActivityBefore(sourceId, currentDay.date, targetId as any);
          if (moved) updateBuilder();
        }
    };

    switch (activity.type) {
      case 'accommodation':
        return <AccommodationCard activity={activity as AccommodationActivity} {...commonProps} />;
      case 'activity':
        return <ActivityCard activity={activity as ActivityActivity} {...commonProps} />;
      case 'flight':
        return <FlightCard activity={activity as FlightActivity} {...commonProps} />;
      case 'transportation':
        return <TransportationCard activity={activity as TransportationActivity} {...commonProps} />;
      case 'food-drinks':
        return <FoodDrinksCard activity={activity as FoodDrinksActivity} {...commonProps} />;
      case 'events':
        return <EventsCard activity={activity as EventsActivity} {...commonProps} />;
      case 'notes':
        return <NotesCard activity={activity as NotesActivity} {...commonProps} />;
      default:
        // Fallback to a generic card for unknown types
        return <ActivityCard activity={activity as ActivityActivity} {...commonProps} />;
    }
  };
  return (
    <>
      <div className="flex flex-col h-full">
        {currentDay ? (
          <>
            {/* Fixed Header Section */}
            <div className="flex-shrink-0 p-4 border-b border-gray-200 bg-white">
              {/* Day Header with Insights and Actions */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col items-start gap-1 flex-shrink-0 sm:min-w-[115px] min-w-[80px]">
                  <h3 className="text-xs sm:text-sm text-gray-900">
                    {formatTitle(currentDay.date)}
                  </h3>
                  <h3 className="text-md sm:text-lg font-semibold text-gray-900 capitalize">{formatDescription(currentDay.date)}</h3>
                </div>

                {/* Day Insights Component - expands to fill available space */}
                <div className="flex-1 flex justify-center items-center overflow-hidden">
                  <DayInsights day={currentDay} />
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Itinerary Insights Modal Button */}
                  {/* <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="icon">
                        <Info className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Itinerary Overview</DialogTitle>
                      </DialogHeader>
                      <ItineraryInsights builder={builder} />
                    </DialogContent>
                  </Dialog> */}

                  {/* Add Activity Dropdown - Only show if not read-only */}
                  {!isReadOnly && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="flex items-center gap-2">
                          <Plus className="h-4 w-4" />
                          <span className="hidden sm:inline">Add Activity</span>
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {ACTIVITIES.filter(type => !type.hidden).map((type) => (
                          <DropdownMenuItem
                            key={type.id}
                            onClick={() => handleAddActivity(type.id as ActivityType)}
                            className="flex items-center gap-2 my-1"
                          >
                            {type.icon}
                            {type.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            </div>

            {/* Scrollable Activities List */}
            <div className="flex-1 overflow-y-auto p-6">
              {currentDay.activities.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {/* External drop zone before the first item to allow inserting at top */}
                  <DropZone
                    key={`drop-start-${currentDay.date}`}
                    visible={isDragging && draggingId !== currentDay.activities[0]?.id}
                    onDrop={(sourceId) => {
                      if (!currentDay) return;
                      // Compute the first activity id that is NOT the source (to avoid using source as target)
                      const firstDifferent = currentDay.activities.find(a => a.id !== sourceId)?.id;
                      // If firstDifferent equals undefined and source was the only item, this is a no-op
                      if (!firstDifferent && currentDay.activities.length <= 1) {
                        setIsDragging(false);
                        return;
                      }
                      // If source is already before firstDifferent (i.e., is the first), no move needed
                      if (currentDay.activities[0]?.id === sourceId) {
                        setIsDragging(false);
                        return;
                      }

                      const moved = builder.moveActivityBefore(sourceId, currentDay.date, firstDifferent);
                      if (moved) updateBuilder();
                      setIsDragging(false);
                      setDraggingId(null);
                    }}
                  />
                  {currentDay.activities.map((activity) => (
                    <div key={activity.id}>
                      { /* If this activity is the one being dragged, render a placeholder (100px height, full width) */ }
                      {draggingId === activity.id ? (
                        <div className="w-full h-[64px] bg-gray-100 rounded-md border-2 border-dashed border-gray-300 mb-2" />
                      ) : (
                        renderActivityCard(activity)
                      )}
                      {/* Drop zone after each card to append after this card */}
                      <DropZone
                        key={`drop-after-${activity.id}`}
                        visible={isDragging && draggingId !== activity.id}
                        onDrop={(sourceId) => {
                          if (!currentDay) return;
                          // move before the next activity id; if this is the last, append
                          const idx = currentDay.activities.findIndex(a => a.id === activity.id);
                          const next = currentDay.activities[idx + 1];
                          const targetId = next ? next.id : undefined;
                          // If target equals source, treat as no-op
                          if (targetId === sourceId) {
                            setIsDragging(false);
                            return;
                          }
                          const moved = builder.moveActivityBefore(sourceId, currentDay.date, targetId as any);
                          if (moved) updateBuilder();
                          setIsDragging(false);
                          setDraggingId(null);
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500 min-h-[500px]">
                  <div className="bg-gray-100 rounded-full p-6 mb-6">
                    <Calendar className="h-12 w-12 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Start building your day</h3>
                  <p className="text-sm text-gray-500 text-center max-w-sm mb-6">
                    Add activities, accommodations, transportation, and more to create the perfect itinerary for this day.
                  </p>
                  {!isReadOnly && (
                    <div className="flex flex-wrap gap-3 justify-center">
                      {ACTIVITIES.filter(type => !type.hidden).map((activity) => (
                        <button
                          key={activity.id}
                          onClick={() => handleAddActivity(activity.id as ActivityType)}
                          className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-colors cursor-pointer"
                        >
                          {activity.icon}
                          <span className="text-xs text-gray-600">{activity.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center py-10">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-sm">Please set your travel dates in the previous step to start building your itinerary.</p>
            </div>
          </div>
        )}
      </div>
      {modalCallbacks.renderModal()}
    </>
  );
};

export default DayBuilderStep;
